// src/components/TaxDetailsDialog.js
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme, alpha } from '@mui/material/styles';
import { Building2, FileText } from 'lucide-react';

const EMPTY = {
  companyName: '',
  contactName: '',
  vatNumber: '',
  registrationNumber: '',
  address: '',
};

/**
 * Asks for the tax details an invoice should be made out to.
 *
 * All of it is optional, and the dialog says so rather than implying it: an
 * invoice addressed to the person who placed the order is a perfectly good
 * invoice, and most customers want exactly that. The fields are here for the
 * ones buying through a business, who need their own VAT number on the document
 * before their accountant will accept it.
 *
 * Nothing is validated beyond a light shape check on the VAT number. We are in
 * no position to verify a customer's tax registration, and refusing to issue an
 * invoice because a number looked unusual would be worse than printing it.
 */
const TaxDetailsDialog = ({
  open,
  order,
  initialDetails,
  alreadyIssued,
  busy,
  onClose,
  onSubmit,
}) => {
  const theme = useTheme();
  const accent = theme.palette.primary.main;

  const [details, setDetails] = useState(EMPTY);
  const [sendEmail, setSendEmail] = useState(false);

  // Reopening for a different order must not carry the last one's answers over.
  useEffect(() => {
    if (!open) return;
    setDetails({ ...EMPTY, ...(initialDetails || {}) });
    setSendEmail(false);
  }, [open, initialDetails]);

  const set = (field) => (event) =>
    setDetails((current) => ({ ...current, [field]: event.target.value }));

  // South African VAT numbers are ten digits starting with a 4. A number that
  // is not one is still submitted — this only says so, in case it was a slip.
  const vatDigits = details.vatNumber.replace(/\D/g, '');
  const vatLooksOdd =
    details.vatNumber.trim() !== '' && !(vatDigits.length === 10 && vatDigits.startsWith('4'));

  const handleSubmit = (withDetails) => () => {
    onSubmit({
      taxDetails: withDetails ? details : {},
      sendEmail,
    });
  };

  const anyDetail = Object.values(details).some((value) => String(value).trim() !== '');

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1.5, pr: 6 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              flexShrink: 0,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              color: accent,
              backgroundColor: alpha(accent, 0.12),
            }}
          >
            <FileText size={19} strokeWidth={1.9} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.3 }}>
              {alreadyIssued ? 'Your invoice' : 'Request a tax invoice'}
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>
              Order {order?.orderNum || ''}
            </Typography>
          </Box>
        </Stack>
        {!busy && (
          <IconButton
            onClick={onClose}
            aria-label="Close"
            size="small"
            sx={{ position: 'absolute', top: 14, right: 14, color: 'text.secondary' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2.5 }}>
        {alreadyIssued && (
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
            This order already has invoice <strong>{alreadyIssued}</strong>. Downloading it
            again gives you the same document; changing the details below reissues it under the
            same number.
          </Alert>
        )}

        <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', lineHeight: 1.65 }}>
          Your invoice is made out to the name and address on the order. If you are buying
          through a business and need its details on the document instead, add them here — every
          field is optional.
        </Typography>

        <Divider sx={{ my: 2.5 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.disabled' }}>
            <Building2 size={13} />
            <Typography
              sx={{
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
              }}
            >
              Business details
            </Typography>
          </Stack>
        </Divider>

        <Stack spacing={2}>
          <TextField
            label="Registered business name"
            placeholder="e.g. Green Acres Farming (Pty) Ltd"
            value={details.companyName}
            onChange={set('companyName')}
            fullWidth
            size="small"
            inputProps={{ maxLength: 120 }}
          />
          <TextField
            label="Contact person"
            placeholder="Who the invoice should be marked for the attention of"
            value={details.contactName}
            onChange={set('contactName')}
            fullWidth
            size="small"
            inputProps={{ maxLength: 120 }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="VAT registration number"
              placeholder="4123456789"
              value={details.vatNumber}
              onChange={set('vatNumber')}
              fullWidth
              size="small"
              inputProps={{ maxLength: 24 }}
              error={vatLooksOdd}
              helperText={
                vatLooksOdd
                  ? 'South African VAT numbers are ten digits beginning with 4 — check this before we issue it.'
                  : ' '
              }
            />
            <TextField
              label="Company registration number"
              placeholder="2019/123456/07"
              value={details.registrationNumber}
              onChange={set('registrationNumber')}
              fullWidth
              size="small"
              inputProps={{ maxLength: 32 }}
              helperText=" "
            />
          </Stack>
          <TextField
            label="Billing address"
            placeholder={'Leave blank to use your delivery address'}
            value={details.address}
            onChange={set('address')}
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            size="small"
            inputProps={{ maxLength: 300 }}
          />
        </Stack>

        <FormControlLabel
          sx={{ mt: 1.5 }}
          control={
            <Checkbox
              checked={sendEmail}
              onChange={(event) => setSendEmail(event.target.checked)}
              size="small"
            />
          }
          label={
            <Typography sx={{ fontSize: '0.88rem' }}>
              Email me a copy as well
            </Typography>
          }
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {/* The way out for the customer who wants an invoice and nothing more:
            one click, no business details, addressed to them. */}
        <Button
          onClick={handleSubmit(false)}
          disabled={busy}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {anyDetail ? 'Skip these details' : 'Use my order details'}
        </Button>
        <Button
          onClick={handleSubmit(true)}
          disabled={busy || !anyDetail}
          variant="contained"
          disableElevation
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999, px: 3 }}
        >
          {busy ? 'Preparing…' : 'Issue invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaxDetailsDialog;
